import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FavoritePageFiltersType } from 'pages/FavoritesPage';
import { useUser } from 'store/User/Context';
import { saleOffersApiRequests } from 'utils/api-requests/sale';
import { isTouchDevice } from 'utils/checkIsTouchDevice';
import SaleContext from 'utils/context/SaleContext';
import { encodeData } from 'utils/encodeAndDecodeFunctions';
import { formatFilterPrice } from 'utils/formatFilterPrice';
import { formatPriceBeforeModification } from 'utils/formatters/formatPriceBeforeModification';
import { getSubModuleByType } from 'utils/formatters/getSubModuleByType';
import { normalizeFlipResult } from 'utils/formatters/normalizeFlipResult';
import { SaleOfferDuplicatesResponse, SaleOfferResponseContent } from 'utils/state-managment/sale/saleOffer';
import { CalculatorSettings } from 'utils/types/Calculator';
import { ModulesType } from 'utils/types/ModulesType';
import { UserReactions } from 'utils/types/OfferData';
import { ReactionType } from 'utils/types/ReactionType';
import { Module } from 'utils/types/SaleModels';
import { hasRole, UserRole } from 'utils/userRoleHelper';

import OfferActions from 'components/common/Card/common/OfferActions/OfferActions';
import {
    InteractiveElements
} from 'components/common/Card/common/V2InteractiveIconsWrapper/V2InteractiveIconsWrapper';
import { IconEnum } from 'components/common/Icon';
import { SliderImages } from 'components/common/Slider/Slider';
import { RaisesTabContentProps } from 'components/common/Tabs/tabs/RaisesTab';
import { V2DuplicatesTabProps } from 'components/common/Tabs/tabs/V2DuplicatesTab';
import { TabType } from 'components/common/Tabs/V2Tabs';
import { convertToThumbnailPath, createImagesForSlider } from 'components/functions/imagesFunctions';
import { saleOfferListFunctions } from 'components/functions/offerListFunctions/saleOfferListFunctions/saleOfferListFunctions';
import { generateOfferActionsElements } from 'components/functions/shared/generateOfferActionsElements';
import { RenderList } from 'components/offerList/V2OfferList';

export const useSalesHook = (filters: FavoritePageFiltersType, activeFilter: ModulesType, subActiveFilter: Module[]) => {
    const {
        saleOfferState,
        loadDuplicates,
        deleteNote,
        updateNote,
        reactionUpdate,
        loadOfferDetailsAsync,
        loadNotesAsync,
        clearOffers,
        loadFavoriteOffers,
        updateOpinion,
        deleteOpinion,
        loadOpinionsAsync,
        handleReservation,
        removeReservation,
        handleNotificationBlockedOfferToggle,
        calculatorState,
        infoElementsState,
        detailsInfoElementsState,
        setDetailsInfoElementClicked,
        setDetailsInfoElementHover,
        setInfoElementHover,
        setInfoElementClicked,
        activeInfoElementId,
        activeDetailInfoElementId,
        updateFlipCalculations,
        handleCalculatorChange,
        resetFlipCalculationField,
        resetFlipCalculations,
        loadFlipCalculations
    } = useContext(SaleContext);
    const [renderList, setRenderList] = useState<RenderList[]>([]);
    const [moreButtonActiveId, setMoreButtonActiveId] = useState<string | null>(null);
    const [organizationActiveId, setOrganizationActiveId] = useState<string | null>(null);
    const { user } = useUser();
    const navigate = useNavigate();
    const hasPredictionRole = hasRole(UserRole.PREDICT_PRICE, user.userRole);
    const hasCalculationRole = hasRole(UserRole.CALCULATOR, user.userRole);

    const handleChangePage = useCallback((currentPage: number, rows = 25, sortType?: string) => {
        loadFavoriteOffers(subActiveFilter, currentPage ?? 1, rows ?? 25, filters, sortType);
    }, [
        filters,
        activeFilter,
        subActiveFilter
    ]);

    const onNoteAdd = () => {
        // TODO: INVEST-257 Czy ta funkcja jest potrzebna?
        // if (!showNote && showLastNote) {
        //     showLastNote();
        // }
        //
        // if (!data.note?.noteId) {
        //     openOfferTab('note');
        // }
    };

    const onMessageClick = () => {
        onNoteAdd();
    };

    const handleDelete = (noteId: number, id: number, type: Module) => {
        deleteNote(id, type, noteId);
    };

    const handleSaveNote = async (value: string, advertId: number, type: Module, id?: number) => {
        updateNote(type, value, advertId, id || 0);
    };

    const toggleContainerDetails = (detailsOpen: boolean, offer: SaleOfferResponseContent) => {
        if (!detailsOpen) {
            if (offer.reaction && !offer.reaction.viewedStatus) {
                reactionUpdate(offer.advertisementId, offer.location!, {
                    ...offer.reaction,
                    openedStatus: true,
                    viewedStatus: true
                } as ReactionType, offer.module);
            }
            loadOfferDetailsAsync(offer.advertisementId, offer.module);
        }
    };

    const handleChangeReaction = (name: keyof ReactionType, value: boolean, offer: SaleOfferResponseContent) => {
        const userReaction = Object.assign({}, offer.reaction);

        if (name === 'likeStatus' && userReaction.unlikeStatus && value) {
            userReaction.unlikeStatus = false;
        } else if (name === 'unlikeStatus' && userReaction.likeStatus && value) {
            userReaction.likeStatus = false;
        }

        (userReaction[name] as boolean) = value;
        const city = offer.location;

        reactionUpdate(offer.advertisementId, city, userReaction as UserReactions, offer.module);
    };

    const loadDetails = (id: number, module: Module) => {
        loadOfferDetailsAsync(id, module);
    };

    const generateInteractiveElements = (offer: SaleOfferResponseContent): InteractiveElements[] => {
        const offerLink = `${window.location.origin}/sale/${getSubModuleByType(offer.module)}/${offer.encryptedId}`;
        const submodule = getSubModuleByType(offer.module);
        const isMoreButtonActive = moreButtonActiveId === offer.advertisementId.toString();
        const similarOfferAction = {
            children: <><p>Pokaż podobne ogłoszenia</p></>,
            onClick: async (e: MouseEvent) => {
                e.stopPropagation();

                const filterData = await saleOffersApiRequests.getSimilarOffersFilter(offer.advertisementId, offer.module);

                if (filterData) {
                    const encoded = encodeData({ ...filterData, locations: filterData.locations || [], _timestamp: Date.now() });
                    window.open(`${window.location.origin}/ai-filter/${encoded}`, '_blank');
                }
            }
        };
        const offerActionsElements = generateOfferActionsElements(offerLink, offer.link, [similarOfferAction]);
        const originalEncryptedId = offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined;
        const originalOfferLink = `${window.location.origin}/sale/${submodule}/${offer.originalEncryptedId}`;

        user.isInOrganization
            ? offerActionsElements.push({
                children:
                <>
                    <p>Prezentuj jako pośrednik</p>
                </>,
                onClick: async (e: MouseEvent) => {
                    e.stopPropagation();

                    navigate(`/sale/${submodule}/${offer.advertisementId}/copy`);
                }
            })
            : null;

        if (originalEncryptedId) {
            offerActionsElements.push({
                children:
                <>
                    <p>Przejdź do oryginalnego ogłoszenia</p>
                </>,
                onClick: async (e: MouseEvent) => {
                    e.stopPropagation();
                    window.open(originalOfferLink, '_blank');
                }
            });
        }

        return [
            {
                tooltipText: 'Wyświetlono',
                icon: IconEnum.EYE,
                active: offer.reaction?.viewedStatus,
                disabled: offer.reaction?.viewedStatus === undefined,
                onClick: () => handleChangeReaction('viewedStatus', !offer.reaction?.viewedStatus, offer)
            },
            {
                tooltipText: 'Lubię to',
                icon: IconEnum.LIKE,
                active: offer.reaction?.likeStatus,
                onClick: () => handleChangeReaction('likeStatus', !offer.reaction?.likeStatus, offer)
            },
            {
                tooltipText: 'Nie lubię tego',
                icon: IconEnum.DISLIKE,
                active: offer.reaction?.unlikeStatus,
                disabled: offer.reaction?.unlikeStatus === undefined,
                onClick: () => handleChangeReaction('unlikeStatus', !offer.reaction?.unlikeStatus, offer)
            },
            {
                tooltipText: 'Notatki',
                icon: IconEnum.MESSAGE,
                active: offer.numberOfNotes ? offer.numberOfNotes > 0 : false,
                counter: offer.numberOfNotes ? offer.numberOfNotes || 1 : undefined,
                openTabOnClick: TabType.NOTES,
                onClick: onMessageClick
            },
            {
                tooltipText: 'Ogłoszenie nieaktualne',
                icon: IconEnum.BLOCKED,
                active: offer.reaction?.hideStatus,
                disabled: offer.reaction?.hideStatus === undefined,
                onClick: () => handleChangeReaction('hideStatus', !offer.reaction?.hideStatus, offer)
            },
            {
                tooltipText: 'Przeprowadzona rozmowa',
                icon: IconEnum.PHONE,
                active: offer.reaction?.phoneStatus,
                disabled: offer.reaction?.phoneStatus === undefined,
                onClick: () => handleChangeReaction('phoneStatus', !offer.reaction?.phoneStatus, offer)
            },
            {
                tooltipText: 'Umówione spotkanie',
                icon: IconEnum.PEOPLES_3,
                active: offer.reaction?.arrangedStatus,
                disabled: offer.reaction?.arrangedStatus === undefined,
                onClick: () => handleChangeReaction('arrangedStatus', !offer.reaction?.arrangedStatus, offer)
            },
            ...offer.phoneNumber
                ? [
                    {
                        icon: IconEnum.PHONE,
                        isLink: true,
                        active: false,
                        isPhoneBtn: false,
                        text: <a href={`tel:${offer.phoneNumber}`}>{offer.phoneNumber}</a>
                    }
                ]
                : [],
            {
                tooltipText: 'Przejdź do ogłoszenia',
                icon: IconEnum.LINK,
                isLink: true,
                noText: true,
                text: <a target="_blank" rel="noopener noreferrer" href={offer.link}/>
            },
            ...offer.photos && offer.photos.length > 0
                ? [
                    {
                        icon: IconEnum.CAMERA,
                        isModalOpen: true,
                        isLink: true,
                        text: <p>Zobacz zdjęcia</p>,
                        isPhotoBtn: true,
                        visibleDuringModal: true,
                        sliderElements: {
                            photos: createImagesForSlider(offer.photos, offer.module) as SliderImages[],
                            title: offer.title
                        }
                    }
                ]
                : [],
            {
                icon: 'squares' as IconEnum,
                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(offer.advertisementId.toString()),
                additionalJSXSibling: isMoreButtonActive ? <OfferActions onClickOutside={() => setMoreButtonActiveId(null)} elements={offerActionsElements} /> : undefined
            }
        ];
    };

    const handleDuplicates = async (offer: SaleOfferResponseContent) => {
        loadDuplicates(offer.module, offer.advertisementId);
    };

    const handleNotes = async (offer: SaleOfferResponseContent) => {
        loadNotesAsync(offer.advertisementId.toString(), offer.module);
    };

    const handleSaveOpinion = (rating: number, opinion: string, id: number, module: Module) => {
        updateOpinion(module, opinion, rating, id);
    };

    const generateOrganizationCardData = (offer: SaleOfferResponseContent) => {
        return {
            isReserved: offer.blockageInfo !== null,
            ...offer.blockageInfo
                ? {
                    reservationDate: offer.blockageInfo.blockedDate,
                    userName: offer.blockageInfo.userName,
                    isPermanentlyReserved: offer.blockageInfo.permanentlyBlocked,
                    myBlockage: offer.blockageInfo.myBlockage
                }

                : [],
            handleReservation: (days: number) => {
                handleReservation(days, offer.advertisementId, offer.module);
                setOrganizationActiveId(offer.advertisementId.toString());
            },
            removeReservation: () => {
                removeReservation(offer.advertisementId, offer.module);
                setOrganizationActiveId(null);
            },
            handleReservationPermanently: () => {
                handleReservation(0, offer.advertisementId, offer.module, true);
                setOrganizationActiveId(offer.advertisementId.toString());
            },
            handleToggleReservation: () => {
                handleNotificationBlockedOfferToggle(offer.advertisementId, offer.module, offer?.blockageInfo?.notificationStatus ?? false);
                setOrganizationActiveId(offer.advertisementId.toString());
            },
            notificationStatus: offer.blockageInfo?.notificationStatus,
            offerId: offer.advertisementId,
            offerModule: offer.module
        };
    };

    const generateCardData = (offer: SaleOfferResponseContent) => {
        const subMainValues: React.ReactNode[] = [];

        if (offer.pricePerSquareMeter) {
            subMainValues.push(
                <><b>{formatFilterPrice(offer.pricePerSquareMeter)?.toString()}</b> zł / m<sup>2</sup></>
            );
        }

        const infoElements = saleOfferListFunctions.generateInfoElements(offer, infoElementsState, hasPredictionRole, setInfoElementHover, setInfoElementClicked);

        const ratingContainer = {
            opinionsNumber: offer.numberOfCommunityOpinions,
            opinionsRating: offer.communityScore,
            isMyOpinion: !!offer.myOpinion,
            commentsCount: offer.numberOfCommunityComments,
            title: offer.title,
            userRatingValue: offer.myOpinion?.numberOfStars,
            userRatingComment: offer.myOpinion?.content || undefined,
            handleGetComments: () => handleNotes(offer),
            handleSendRating: (rating: number, description: string) => offer.myOpinion ? updateOpinion(offer.module, description, rating, offer.advertisementId, offer.myOpinion?.id) : handleSaveOpinion(rating, description, offer.advertisementId, offer.module)
        };

        return {
            preTitle: offer.location,
            additionalPreTitleString: offer.predictedLocation ? offer.predictedLocation : undefined,
            lowestPriceLabel: offer.duplicateWithLowestPrice,
            privateOfferLabel: offer.duplicateWithPrivateOffer,
            archivedLabel: offer.archived,
            oldPrice: formatPriceBeforeModification(offer.priceBeforeModification),
            date: offer.dateAdded,
            dateIcon: saleOfferListFunctions.getDateIcon(offer.status),
            title: offer.title,
            infoElements: infoElements,
            withPrediction: true,
            interactiveElements: generateInteractiveElements(offer),
            modifications: {
                duplicationsNumber: offer.numberOfDuplicates ?? 0,
                modificationsNumber: offer.numberOfModifications ?? 0,
                boostsNumber: offer.numberOfRaises ?? 0
            },
            note: offer.note,
            numberOfNotes: offer.numberOfNotes ? offer.numberOfNotes : undefined,
            getAllNotes: loadNotesAsync,
            showLendiButtons: true,
            offerId: offer.advertisementId,
            offerModule: offer.module,
            adContext: { adId: offer.advertisementId, adModule: offer.module },
            modificationDate: offer.lastUpdated,
            thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : './defaultImg.png',
            ratingScore: offer.score ?? 0,
            scorePrecision: offer.scorePrecision ?? 0,
            additionalInfoBadge: offer.offerFrom,
            predictedAdditionalInfoBadge: offer.predictedOfferFrom,
            images: createImagesForSlider(offer.photos || [], offer.module),
            mainValue: offer.price ? `${formatFilterPrice(offer.price)} zł` : '- zł',
            subMainValues,
            calculationValue: offer.flipResult && offer.flipResult.maxPurchasePrice ? offer.flipResult.maxPurchasePrice.toString() : undefined,
            hasCalculationRole,
            link: {
                url: offer.link,
                name: offer.portal
            },
            ...!user.isInOrganization
                ? { ratingContainer }
                : { organizationAndRating: {
                    ratingContainer: user.isInOrganization && offer.blockageInfo?.permanentlyBlocked ? undefined : ratingContainer,
                    organizationContainer: generateOrganizationCardData(offer)
                } },
            predictedPrice: offer.module === Module.SALE_FLAT && offer.predictedPrice ? offer.predictedPrice.toString() : undefined,
            hasPredictionRole,
            isDuplicate: offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined
        };
    };

    const generateDuplicateCardData = (offer: SaleOfferDuplicatesResponse) => {
        return {
            preTitle: offer.predictedLocation ? `${offer.location}, ` : offer.location,
            additionalPreTitleString: offer.predictedLocation ? offer.predictedLocation : undefined,
            oldPrice: formatPriceBeforeModification(offer.priceBeforeModification),
            date: offer.dateAdded,
            title: offer.title,
            infoElements: saleOfferListFunctions.generateInfoElements(offer as SaleOfferResponseContent, infoElementsState, hasPredictionRole, setInfoElementHover, setInfoElementClicked),
            interactiveElements: generateInteractiveElements(offer as SaleOfferResponseContent),
            modifications: {
                modificationsNumber: offer.numberOfModifications ?? 0,
                boostsNumber: offer.numberOfRaises ?? 0
            },
            note: offer.note,
            modificationDate: offer.lastUpdated,
            thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module!) : './defaultImg.png',
            ratingScore: offer.score ?? 0,
            additionalInfoBadge: offer.offerFrom,
            images: createImagesForSlider(offer.photos || [], offer.module!),
            mainValue: offer.price ? `${offer.price} zł` : '- zł',
            calculationValue: offer.flipResult && offer.flipResult.maxPurchasePrice ? offer.flipResult.maxPurchasePrice.toString() : undefined,
            hasCalculationRole,
            link: {
                url: offer.link,
                name: offer.portal
            },
            predictedPrice: offer.module === Module.SALE_FLAT && offer.predictedPrice ? offer.predictedPrice.toString() : undefined,
            hasPredictionRole
        };
    };

    useEffect(() => {
        const offersList = saleOfferState.saleOfferList.content
            ? saleOfferState.saleOfferList?.content.map((offer): RenderList => {
                const isMoreButtonActive = moreButtonActiveId === `${offer.advertisementId}-detail`;
                const offerLink = `${window.location.origin}/sale/${getSubModuleByType(offer.module)}/${offer.encryptedId}`;
                const similarOfferAction = {
                    children: <><p>Pokaż podobne ogłoszenia</p></>,
                    onClick: async (e: MouseEvent) => {
                        e.stopPropagation();

                        const filterData = await saleOffersApiRequests.getSimilarOffersFilter(offer.advertisementId, offer.module);

                        if (filterData) {
                            const encoded = encodeData({ ...filterData, locations: filterData.locations || [], _timestamp: Date.now() });
                            window.open(`${window.location.origin}/ai-filter/${encoded}`, '_blank');
                        }
                    }
                };
                const offerActionsElements = generateOfferActionsElements(offerLink, offer.link, [similarOfferAction]);

                const interactiveElements = generateInteractiveElements(offer);
                interactiveElements.splice(-1);
                const detailsTabContent = saleOfferListFunctions.generateOfferDetailsTabContent(offer, detailsInfoElementsState, hasPredictionRole, setDetailsInfoElementHover, setDetailsInfoElementClicked);
                const duplicatesTabContent: V2DuplicatesTabProps = {
                    duplicates: offer.duplicates?.map((duplicate) => {
                        return {
                            id: duplicate.advertisementId,
                            bigImage: true,
                            cardData: generateDuplicateCardData({ ...duplicate, module: offer.module }),
                            onClick: () => {},
                            openOfferTab: () => {}
                        };
                    })
                };
                const flipInfo = normalizeFlipResult(offer.flipResult);

                return {
                    id: offer.advertisementId,
                    onSingleOfferClick: (_: React.MouseEvent, detailsOpen: boolean) => toggleContainerDetails(detailsOpen, offer),
                    baseCardContent: {
                        bigImage: true,
                        cardData: generateCardData(offer),
                        openOfferTab: () => {
                        }
                    },
                    detailsCardData: {
                        interactiveElements: [
                            ...interactiveElements, {
                                icon: 'squares' as IconEnum,
                                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(`${offer.advertisementId}-detail`),
                                additionalJSXSibling: isMoreButtonActive ? <OfferActions onClickOutside={() => setMoreButtonActiveId(null)} isInModal elements={offerActionsElements} /> : undefined
                            }
                        ],
                        modificationDate: offer.lastUpdated,
                        additionalInfoBadge: offer.offerFrom,
                        thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : './defaultImg.png',
                        date: offer.dateAdded!,
                        dateIcon: saleOfferListFunctions.getDateIcon(offer.status),
                        showLendiButton: true,
                        onToggleContainerDetails: () => {
                        },
                        tabsContent: {
                            toggleContainerDetails: () => {
                            },
                            loadDetails: () => loadDetails(offer.advertisementId, offer.module),
                            interactiveElements,
                            detailsTabContent: {
                                ...detailsTabContent,
                                hasCalculationRole,
                                hasPredictionRole,
                                withPrediction: true,
                                score: offer.score,
                                scorePrecision: offer.scorePrecision,
                                showLendiButton: true,
                                isDuplicate: offer.originalEncryptedId !== null && offer.originalEncryptedId !== undefined,
                                lowestPriceLabel: offer.duplicateWithLowestPrice,
                                privateOfferLabel: offer.duplicateWithPrivateOffer,
                                archivedLabel: offer.archived,
                                adContext: { adId: offer.advertisementId, adModule: offer.module },
                                ...user.organizationRole ? { organizationContainer: generateOrganizationCardData(offer) } : {}
                            },
                            duplicatesTabContent,
                            modificationTabContent: {
                                modifications: offer.detailedContent?.modifications || []
                            },
                            notesTabContent: {
                                notes: saleOfferState.notes,
                                handleDelete: (noteId: string) => handleDelete(Number(noteId), offer.advertisementId, offer.module),
                                handleSaveNote: (value: string, id?: string) => handleSaveNote(value, offer.advertisementId, offer.module, Number(id))
                            },
                            calculationsTabContent: offer.module === Module.SALE_FLAT
                                ? {
                                    calculation: true,
                                    hasCalculationRole,
                                    id: offer.advertisementId,
                                    flipInfo,
                                    currentPrice: offer.price,
                                    loadFlipCalculations: () => loadFlipCalculations(offer.advertisementId),
                                    calculatorState,
                                    onValueChange: handleCalculatorChange,
                                    onUpdateField: (fieldName: keyof CalculatorSettings, value: string) => updateFlipCalculations(offer.advertisementId, {
                                        ...calculatorState,
                                        [fieldName]: value
                                    }),
                                    onResetField: (fieldName: keyof CalculatorSettings) => resetFlipCalculationField(offer.advertisementId, fieldName),
                                    onResetAll: () => resetFlipCalculations(offer.advertisementId)
                                }
                                : undefined,
                            opinionTabContent: {
                                handleSaveOpinion: (rating: number, content: string, id?: number) => updateOpinion(offer.module, content, rating, offer.advertisementId, id || offer.myOpinion?.id),
                                comments: saleOfferState.scoreAndOpinions?.opinions?.map((opinion) => ({
                                    id: opinion.opinionId,
                                    date: opinion.dateAdded,
                                    content: opinion.content || '',
                                    rating: opinion.numberOfStars,
                                    isUserComment: opinion.loggedUserOpinion
                                })),
                                applicationScore: offer.score,
                                applicationScorePrecision: offer.scorePrecision,
                                commentsCount: offer.numberOfCommunityComments,
                                opinionsNumber: offer.numberOfCommunityOpinions,
                                opinionsRating: saleOfferState.scoreAndOpinions?.communityScore || 0,
                                handleDeleteOpinion: (id: number) => deleteOpinion(offer.advertisementId, offer.module, id)
                            },
                            raisesTabContent: offer.detailedContent && offer.detailedContent.raisesValue && offer.detailedContent.raisesValue.length > 0 ? offer.detailedContent.raisesValue as RaisesTabContentProps : undefined,
                            numberOfModifications: offer.numberOfModifications || 0,
                            numberOfDuplicates: offer.numberOfDuplicates || 0,
                            numberOfNotes: offer.numberOfNotes || 0,
                            numberOfRaises: offer.numberOfRaises || 0,
                            handleLoadDuplicates: () => handleDuplicates(offer),
                            handleLoadNotes: () => handleNotes(offer),
                            handleLoadComments: () => loadOpinionsAsync(offer.advertisementId, offer.module)
                        }
                    }
                };
            })
            : [];

        setRenderList(offersList);

        return () => {
            if (location.pathname !== '/favorites') {
                clearOffers();
            }
        };
    }, [
        saleOfferState.saleOfferList?.content,
        saleOfferState.notes,
        saleOfferState.scoreAndOpinions?.opinions,
        moreButtonActiveId,
        organizationActiveId,
        calculatorState,
        activeInfoElementId,
        infoElementsState,
        activeDetailInfoElementId
    ]);

    const handleFavoriteSearch = (modules: Module[], page?: number, size?: number, sortType?: string) => {
        clearOffers();
        loadFavoriteOffers(modules, page ?? 1, size ?? 25, filters, sortType);
    };

    return {
        saleOfferState,
        handleChangePage,
        handleFavoriteSearch,
        renderList
    };
};

